import { __decorate, __metadata } from 'tslib';
import { DOCUMENT, NgFor } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, ElementRef, Component, ChangeDetectionStrategy, Input, HostBinding, QueryList, signal, NgZone, ChangeDetectorRef, Renderer2, afterNextRender, ViewChild, ViewChildren } from '@angular/core';
import { RouterLink, Router, Scroll } from '@angular/router';
import { isPresent } from '@ng-doc/core/helpers/is-present';
import { ngDocZoneOptimize } from '@ng-doc/ui-kit';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { fromEvent, merge } from 'rxjs';
import { filter, map, startWith, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NgDocDecodeUriComponentPipe } from '@ng-doc/app/pipes/decode-uri-component';
const _c0 = ["ng-doc-toc-element", ""];
const _c1 = ["*"];
const _c2 = () => [];
const _c3 = ["selection"];
function NgDocTocComponent_For_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 5);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r1 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("path", item_r1.path)("hash", item_r1.hash)("level", item_r1.level)("selected", item_r1 === ctx_r1.activeItem());
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r1.title, " ");
  }
}
let NgDocTocElementComponent = /*#__PURE__*/(() => {
  class NgDocTocElementComponent {
    constructor() {
      this.path = '';
      this.hash = '';
      this.selected = false;
      this.level = 1;
      this.elementRef = inject(ElementRef);
    }
    static {
      this.ɵfac = function NgDocTocElementComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocTocElementComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgDocTocElementComponent,
        selectors: [["li", "ng-doc-toc-element", ""]],
        hostVars: 2,
        hostBindings: function NgDocTocElementComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-ng-doc-selected", ctx.selected)("data-ng-doc-level", ctx.level);
          }
        },
        inputs: {
          path: "path",
          hash: "hash",
          selected: "selected",
          level: "level"
        },
        attrs: _c0,
        ngContentSelectors: _c1,
        decls: 3,
        vars: 7,
        consts: [[3, "routerLink", "fragment"]],
        template: function NgDocTocElementComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "a", 0);
            i0.ɵɵpipe(1, "decodeUriComponent");
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵstyleProp("padding-left", "calc(var(--ng-doc-toc-indent) * " + ctx.level + ")");
            i0.ɵɵproperty("routerLink", i0.ɵɵpureFunction0(6, _c2))("fragment", i0.ɵɵpipeBind1(1, 4, ctx.hash));
          }
        },
        dependencies: [RouterLink, NgDocDecodeUriComponentPipe],
        styles: ["[_nghost-%COMP%]{display:flex;margin:0;color:var(--ng-doc-text)}[data-ng-doc-level=\"1\"][_nghost-%COMP%]   a[_ngcontent-%COMP%]{padding-left:var(--ng-doc-base-gutter)}[data-ng-doc-selected=true][_nghost-%COMP%]{color:var(--ng-doc-primary)}[_nghost-%COMP%]:hover{cursor:pointer;color:var(--ng-doc-primary)}[_nghost-%COMP%]   a[_ngcontent-%COMP%]{font-family:var(--ng-doc-font-family);font-variant:no-contextual;color:var(--ng-doc-text);line-height:var(--ng-doc-line-height);font-size:var(--ng-doc-font-size);font-weight:var(--ng-doc-font-weight);padding:calc(var(--ng-doc-base-gutter) / 2);color:inherit;width:100%;text-decoration:none;word-break:break-word;--ng-doc-font-size: 14px}"],
        changeDetection: 0
      });
    }
  }
  return NgDocTocElementComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgDocTocComponent = /*#__PURE__*/(() => {
  let NgDocTocComponent = class NgDocTocComponent {
    constructor() {
      this.tableOfContent = [];
      this.elements = new QueryList();
      this.activeItem = signal(undefined);
      this.document = inject(DOCUMENT);
      this.ngZone = inject(NgZone);
      this.changeDetectorRef = inject(ChangeDetectorRef);
      this.renderer = inject(Renderer2);
      this.router = inject(Router);
      afterNextRender({
        write: () => {
          const scrollSelection = fromEvent(this.document, 'scroll').pipe(filter(() => !!this.tableOfContent.length), map(event => event.target.scrollingElement), startWith(this.document.scrollingElement), map(target => {
            const percentage = target.scrollTop * 100 / (target.scrollHeight - target.offsetHeight);
            const selectionLine = target.scrollTop + target.offsetHeight * percentage / 100;
            if (!this.tableOfContent.length) {
              return null;
            }
            return this.tableOfContent.reduce((pTarget, cTarget) => {
              const pTop = pTarget.element.getBoundingClientRect().top + target.scrollTop;
              const cTop = cTarget.element.getBoundingClientRect().top + target.scrollTop;
              return Math.abs(cTop - selectionLine) < Math.abs(pTop - selectionLine) ? cTarget : pTarget;
            });
          }), filter(isPresent));
          const routerSelection = this.router.events.pipe(map(event => {
            if (event instanceof Scroll) {
              const item = this.tableOfContent.find(item => item.path.includes(event.routerEvent.url));
              if (item) {
                return item;
              }
            }
            return null;
          }), filter(isPresent), debounceTime(20));
          const elementsChanges = this.elements.changes.pipe(map(() => this.activeItem()), filter(isPresent));
          merge(merge(scrollSelection, routerSelection).pipe(distinctUntilChanged()), elementsChanges).pipe(debounceTime(0), ngDocZoneOptimize(this.ngZone), untilDestroyed(this)).subscribe(this.select.bind(this));
        }
      });
    }
    ngOnInit() {
      this.activeItem.set(this.tableOfContent[0]);
    }
    /**
     * Selects the item in the table of content.
     * @param item - Item to select.
     */
    select(item) {
      const index = this.tableOfContent.indexOf(item);
      if (this.selection) {
        const element = this.elements.toArray()[index]?.elementRef.nativeElement;
        if (element) {
          this.renderer.setStyle(this.selection.nativeElement, 'top', `${element.offsetTop}px`);
          this.renderer.setStyle(this.selection.nativeElement, 'height', `${element.offsetHeight}px`);
          element.scrollIntoView({
            block: 'nearest'
          });
        }
      }
      this.activeItem.set(item);
    }
    static {
      this.ɵfac = function NgDocTocComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocTocComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgDocTocComponent,
        selectors: [["ng-doc-toc"]],
        viewQuery: function NgDocTocComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c3, 5, ElementRef);
            i0.ɵɵviewQuery(NgDocTocElementComponent, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.selection = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.elements = _t);
          }
        },
        inputs: {
          tableOfContent: "tableOfContent"
        },
        decls: 7,
        vars: 0,
        consts: [["selection", ""], [1, "ng-doc-toc-wrapper"], [1, "ng-doc-toc-container"], [1, "ng-doc-toc-selection"], [1, "ng-doc-toc"], ["ng-doc-toc-element", "", 3, "path", "hash", "level", "selected"]],
        template: function NgDocTocComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 1)(1, "div", 2);
            i0.ɵɵelement(2, "div", 3, 0);
            i0.ɵɵelementStart(4, "ul", 4);
            i0.ɵɵrepeaterCreate(5, NgDocTocComponent_For_6_Template, 2, 5, "li", 5, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵelementEnd()()();
          }
          if (rf & 2) {
            i0.ɵɵadvance(5);
            i0.ɵɵrepeater(ctx.tableOfContent);
          }
        },
        dependencies: [NgDocTocElementComponent],
        styles: ["[_nghost-%COMP%]   .ng-doc-toc-wrapper[_ngcontent-%COMP%]{position:relative;width:var(--ng-doc-toc-width)}[_nghost-%COMP%]   .ng-doc-toc-wrapper[_ngcontent-%COMP%]   .ng-doc-toc-container[_ngcontent-%COMP%]{position:fixed;top:calc(var(--ng-doc-navbar-height) + var(--ng-doc-toc-top-padding));overflow-y:auto;height:calc(100% - var(--ng-doc-navbar-height) - var(--ng-doc-base-gutter) * 5);width:var(--ng-doc-toc-width)}[_nghost-%COMP%]   .ng-doc-toc-wrapper[_ngcontent-%COMP%]   .ng-doc-toc-selection[_ngcontent-%COMP%]{position:absolute;transform:translate(-50%);width:3px;border-radius:calc(var(--ng-doc-base-gutter) / 2);background-color:var(--ng-doc-primary);left:calc(var(--ng-doc-toc-margin) + 1px);transition:var(--ng-doc-transition)}[_nghost-%COMP%]   .ng-doc-toc-wrapper[_ngcontent-%COMP%]   .ng-doc-toc[_ngcontent-%COMP%]{list-style:none;margin:0 0 0 var(--ng-doc-toc-margin);border-left:1px solid var(--ng-doc-base-3);padding:0 0 0 var(--ng-doc-base-gutter)}@media (max-width: 1200px){[_nghost-%COMP%]   .ng-doc-toc-wrapper[_ngcontent-%COMP%]{display:none}}"],
        changeDetection: 0
      });
    }
  };
  NgDocTocComponent = __decorate([UntilDestroy(), __metadata("design:paramtypes", [])], NgDocTocComponent);
  return NgDocTocComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgDocTocComponent, NgDocTocElementComponent };
